import { useEffect, useRef, useState } from "react";
import { Loader2, Upload, Trash2, ArrowUp, ArrowDown, Save, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

export function HeroAdmin() {
  const [slides, setSlides] = useState([]);
  const [intervalSec, setIntervalSec] = useState(6);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/shop/admin/hero").then((r) => { setSlides(r.data?.slides || []); setIntervalSec(r.data?.interval_sec || 6); }).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const upd = (i, k, v) => setSlides((s) => s.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const move = (i, d) => setSlides((s) => { const n = [...s]; const j = i + d; if (j < 0 || j >= n.length) return n; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const remove = (i) => setSlides((s) => s.filter((_, idx) => idx !== i));

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/files", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setSlides((s) => [...s, { file_id: data.id, url: "", alt: "", href: "", enabled: true }]);
      }
      toast.success("Dodano baner — ustaw link i zapisz");
    } catch (err) { toast.error(errMsg(err)); } finally { setUploading(false); }
  };

  const save = async () => {
    setBusy(true);
    try { await api.put("/shop/admin/hero", { slides, interval_sec: intervalSec }); toast.success("Zapisano banery sklepu"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const srcOf = (s) => s.file_id ? `${API}/files/${s.file_id}` : s.url;

  if (loading) return <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />;

  return (
    <div className="space-y-4" data-testid="hero-admin">
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="hero-upload" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-2 bg-sky-400 text-[#0D1117] hover:bg-sky-500 font-bold">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Dodaj baner
        </Button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} />
        <span className="text-xs text-muted-foreground">Zalecany baner panoramiczny ~2000×667 px (proporcje 3:1). Kolejność = kolejność wyświetlania w sliderze.</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-sky-400/30 bg-sky-400/5 p-3">
        <Label className="text-sm font-semibold">Automatyczna zmiana slajdu co</Label>
        <Input data-testid="hero-interval" type="number" min={2} max={30} value={intervalSec}
          onChange={(e) => setIntervalSec(Math.max(2, Math.min(30, parseInt(e.target.value, 10) || 6)))}
          className="h-9 w-20 text-center" />
        <span className="text-sm text-muted-foreground">sekund (2–30). Zapisz, aby zastosować.</span>
      </div>

      {slides.length === 0 ? (
        <p className="text-sm text-muted-foreground">Brak banerów. Dodaj pierwszy — do czasu dodania sklep pokazuje banery domyślne.</p>
      ) : (
        <div className="space-y-3">
          {slides.map((s, i) => (
            <div key={i} data-testid={`hero-row-${i}`} className="flex flex-col sm:flex-row gap-3 rounded-xl border border-border bg-secondary p-3">
              <div className="w-full sm:w-64 shrink-0 rounded-lg overflow-hidden bg-white/5 border border-border">
                {srcOf(s) ? <img src={srcOf(s)} alt="" className="w-full h-auto block" /> : <div className="aspect-[3/1] grid place-items-center text-muted-foreground text-xs">Brak obrazu</div>}
              </div>
              <div className="flex-1 space-y-2">
                <div className="grid sm:grid-cols-2 gap-2">
                  <div><Label className="text-xs">Opis (alt)</Label><Input data-testid={`hero-alt-${i}`} value={s.alt || ""} onChange={(e) => upd(i, "alt", e.target.value)} className="mt-1 h-9" placeholder="np. Kolekcja zimowa" /></div>
                  <div><Label className="text-xs">Link po kliknięciu (opcjonalnie)</Label><Input data-testid={`hero-href-${i}`} value={s.href || ""} onChange={(e) => upd(i, "href", e.target.value)} className="mt-1 h-9" placeholder="/sklep lub https://…" /></div>
                </div>
                <div className="flex flex-wrap items-center gap-4 pt-1">
                  <label className="flex items-center gap-2 text-sm"><Switch data-testid={`hero-enabled-${i}`} checked={s.enabled !== false} onCheckedChange={(v) => upd(i, "enabled", v)} />Aktywny</label>
                  <div className="flex items-center gap-1">
                    <Button data-testid={`hero-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} size="sm" variant="outline" className="h-8 px-2"><ArrowUp className="h-4 w-4" /></Button>
                    <Button data-testid={`hero-down-${i}`} onClick={() => move(i, 1)} disabled={i === slides.length - 1} size="sm" variant="outline" className="h-8 px-2"><ArrowDown className="h-4 w-4" /></Button>
                  </div>
                  <Button data-testid={`hero-del-${i}`} onClick={() => remove(i)} size="sm" variant="outline" className="h-8 px-2 border-rose-500/40 text-rose-400 ml-auto"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 border-t border-border pt-4">
        <Button data-testid="hero-save" onClick={save} disabled={busy} className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Zapisz banery</Button>
        <a href="/sklep" target="_blank" rel="noreferrer"><Button variant="outline" size="sm" className="gap-2"><ExternalLink className="h-4 w-4" />Podgląd sklepu</Button></a>
      </div>
    </div>
  );
}
